/**
 * @file The one renderer for an analytics refusal or failure, for a whole
 * query or one refused column. Consumers: `AnalyticsUnansweredFigure` and the tab
 * panels' refused columns. `notConfigured` shows the missing key in a
 * tooltip; an `invalidRequest` whose key is `query` or absent is a query id
 * the deployed service does not know yet; `noTeam` reads "No team assigned";
 * a failure shows its message. It never renders a number, so a refusal never
 * reads as zero.
 */
import { FC } from 'react';
import { Tooltip, Typography } from 'antd';
import type { Snapshot } from 'valtio';

import type { AnalyticsAnswer } from '@/analytics';
import type { NamedQueryRefusedColumn } from '#types';

const { Text } = Typography;

type AnalyticsRefusalProps =
  | {
      answer: Snapshot<
        Extract<AnalyticsAnswer, { status: 'refused' | 'failed' }>
      >;
    }
  | { column: NamedQueryRefusedColumn };

const NotConfigured: FC<{ settingKey: string | undefined }> = ({
  settingKey,
}) => (
  <Tooltip
    title={
      settingKey
        ? `Missing setting: ${settingKey}`
        : 'The analytics service did not name the missing setting.'
    }
  >
    <Text type="secondary">Not configured</Text>
  </Tooltip>
);

export const AnalyticsRefusal: FC<AnalyticsRefusalProps> = (props) => {
  if ('column' in props) return <NotConfigured settingKey={props.column.key} />;
  const { answer } = props;
  if (answer.status === 'failed')
    return <Text type="danger">{answer.message}</Text>;
  const { refusal, key } = answer.refusal;
  switch (refusal) {
    case 'notConfigured':
      return <NotConfigured settingKey={key} />;
    case 'factTableOutdated':
      return <Text type="secondary">Numbers are being rebuilt</Text>;
    case 'forbiddenLevel':
      return (
        <Text type="secondary">This level needs a right you do not have.</Text>
      );
    case 'noTeam':
      return <Text type="secondary">No team assigned</Text>;
    case 'invalidRequest':
      return key === undefined || key === 'query' ? (
        <Tooltip title="The deployed analytics service does not know this query yet.">
          <Text type="secondary">Not available yet</Text>
        </Tooltip>
      ) : (
        <Text type="secondary">{answer.message}</Text>
      );
  }
};
